import { apiClient } from './apiClient';

export interface SupportTicket {
  id: number;
  shop_id?: number;
  subject: string;
  description: string;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  created_at: string;
  updated_at: string;
  shop?: {
    id: number;
    shop_name: string;
  };
  replies?: SupportReply[];
}


// Add Support Reply
export interface SupportReply {
  id: number;
  ticket_id: number;
  user_id: number;
  message: string;
  is_admin: boolean;
  attachment_url?: string | string[];
  created_at: string;
}

export const supportTicketService = {
  // Create a new support ticket
  createTicket: async (data: {
    subject: string;
    description: string;
    category: string;
    priority: string;
  }): Promise<SupportTicket> => {
    const response = await apiClient.post<{ data: SupportTicket }>('/support-tickets', data);
    return response.data;
  },

  // Create a new support ticket with file attachment
  createTicketWithAttachment: async (formData: FormData): Promise<SupportTicket> => {
    const response = await apiClient.post<{ data: SupportTicket }>('/support-tickets', formData);
    return response.data;
  },

  // Get tickets for current shop owner
  getMyTickets: async (filters?: {
    status?: string;
    search?: string;
    page?: number;
  }): Promise<{ data: SupportTicket[]; pagination: any }> => {
    const response = await apiClient.get<{ data: SupportTicket[]; pagination: any }>('/support-tickets/my-tickets', { 
      params: filters 
    });
    return response;
  },

  // Get all tickets (admin only)
  getAllTickets: async (filters?: {
    status?: string;
    priority?: string;
    search?: string;
    page?: number;
  }): Promise<{ data: SupportTicket[]; pagination: any }> => {
    const response = await apiClient.get<{ data: SupportTicket[]; pagination: any }>('/support-tickets', { 
      params: filters 
    });
    return response;
  },

  // Get single ticket details
  getTicket: async (id: number): Promise<SupportTicket> => {
    const response = await apiClient.get<{ data: SupportTicket }>(`/support-tickets/${id}`);
    return response.data;
  },

  // Add reply to ticket
  addReply: async (ticketId: number, message: string): Promise<SupportReply> => {
    const response = await apiClient.post<{ data: SupportReply }>(`/support-tickets/${ticketId}/replies`, {
      message
    });
    return response.data;
  },

  // Add reply with attachments
  addReplyWithAttachment: async (ticketId: number, formData: FormData): Promise<SupportReply> => {
    const response = await apiClient.post<{ data: SupportReply }>(`/support-tickets/${ticketId}/replies`, formData);
    return response.data;
  },

  // Update ticket status (admin only)
  updateTicketStatus: async (id: number, status: string): Promise<SupportTicket> => {
    const response = await apiClient.patch<{ data: SupportTicket }>(`/support-tickets/${id}`, { status });
    return response.data;
  },

  // Close ticket
  closeTicket: async (id: number): Promise<SupportTicket> => {
    const response = await apiClient.post<{ data: SupportTicket }>(`/support-tickets/${id}/close`, {});
    return response.data;
  },

  // Get ticket categories
  getCategories: async (): Promise<Record<string, string>> => {
    const response = await apiClient.get<{ data: Record<string, string> }>('/support-tickets/categories');
    return response.data;
  }
};
